import _ from 'lodash';
import store from '@/store';
import { generateThresholdCoupon, processCoupon } from '@/views/config/coupon/helper/utils';
import { couponFetchStatus, couponSourceTypeMap, couponTypes } from '@/views/config/coupon/helper/constant';
import { isEmptyVal } from '@/utils';
import { compareDate, getNextPstCycleDate } from '@/utils/dateformat';
import { BILLING_CYCLE } from '@/common/constants';

/**
 *
 * @return {CouponDiscountMapResultBean}
 */
const getDefaultDiscountResult = () => {
  return {
    annualDiscount: 0,
    monthlyDiscount: 0,
    totalDiscount: 0,
    discountPlans: {},
    thresholdResult: {},
  };
};

/**
 *
 * @return {CouponDiscountMapResultBean}
 */
const computedDiscountForAccountCoupon = () => {
  return {
    ...getDefaultDiscountResult(),
    totalDiscount: Math.abs(Number(store.getters?.couponState.account_discount)),
  };
};

/**
 * @description compute with timebase coupon
 * @return {CouponDiscountMapResultBean}
 */
const computedDiscountForTimeBaseCoupon = () => {
  const { getSelectedPlans, getSelectedPlansAmount, couponState } = store.getters;
  const planWithAmountMap = getSelectedPlansAmount?.plans;
  const selectedPlanList = getSelectedPlans;

  const discountPlanList = _.chain(couponState?.timeBase)
    .reduce((pre, item) => {
      // couponPeriod --> coupon period
      // couponDefType --> 1, 2 -> mlm, 3 -> marketing,  4 -> cancellation, 5 -> renew
      // discountType --> coupon discount type --> 1 percentage 2 fixed amount 5 threshold
      // price_mapping_plan_code --> mapping increase plan code
      // plan_code, price_mapping_plan_code, expireDate, discount, discountType, couponPeriod, couponDefType
      const { plan_code: planCode, price_mapping_plan_code, ...rest } = item;

      return [
        ...pre,
        { planCode, ...rest },
        price_mapping_plan_code ? { planCode: price_mapping_plan_code, ...rest } : undefined
      ];
    }, [])
    .filter(Boolean)
    .value();

  return processCoupon({
    discountPlanList,
    selectedPlanList,
    planWithAmountMap,
  });
};

/**
 *
 * @return {CouponDiscountMapResultBean}
 */
const computedDiscountForMarketingCoupon = () => {
  const { couponState, getSelectedPlans, getSelectedPlansAmount } = store.getters;
  const { couponSourceType } = couponState;

  // calculate with threshold coupon
  if (couponSourceType === couponSourceTypeMap.threshold) {
    return generateThresholdCoupon(couponState, store.getters);
  }

  const selectedPlanList = getSelectedPlans;
  const planWithAmountMap = getSelectedPlansAmount?.plans;
  // in order to fix plan increase case (ex: zoom one pro)
  const fullDiscountInfo = { ...couponState?.discountInfo, ...couponState?.discountInfoExtension };

  const discountPlanList = _.chain(fullDiscountInfo)
    .keys()
    .map((planCode) => {
      const {
        expireDate,
        discount,
        couponDiscountType: discountType,
        couponPeriod,
        couponDefType = couponTypes.MARKETING
      } = _.get(fullDiscountInfo, [planCode], {});

      return {
        planCode,
        expireDate,
        discount,
        discountType,
        couponPeriod,
        couponDefType,
      };
    })
    .value();

  return processCoupon({
    discountPlanList,
    selectedPlanList,
    planWithAmountMap,
  });
};

/**
 * @description Calculate the corresponding discount according to the different types defined by coupon
 * @return {CouponDiscountMapResultBean}
 */
export const computeDiscountWithAmountMap = () => {
  const { couponState = {}, verifyCouponStatus } = store.getters;
  const { couponType } = couponState;

  if (couponType === couponTypes.ACCOUNT) {
    return computedDiscountForAccountCoupon();
  }

  if (couponType === couponTypes.TIMEBASE) {
    return computedDiscountForTimeBaseCoupon();
  }

  if (couponType === couponTypes.MARKETING && verifyCouponStatus === couponFetchStatus.successfully) {
    return computedDiscountForMarketingCoupon();
  }

  return getDefaultDiscountResult();
};


export const computedNextBillAmount = () => {
  const getters = store.getters;
  const { couponSourceType } = getters.couponState
  const { discountPlans: planWithDiscount } = getters.discountWithAmountMap;
  const { annualAmount, monthlyAmount, quarterlyAmount, twoYearsAmount, threeYearsAmount } =
    getters.getSelectedPlansAmount;
  const defaultConfig = {
    monthlyDiscount: 0,
    annualDiscount: 0,
    quarterlyDiscount: 0,
    twoYearsDiscount: 0,
    threeYearsDiscount: 0,
  };

  // exclude threshold coupon and discountList not empty
  if (![couponSourceTypeMap.threshold].includes(couponSourceType) && !isEmptyVal(planWithDiscount)) {
    const selectedPlanMap = getters.getSelectedPlansMap;

    const discountMap = _.reduce(_.keys(planWithDiscount), function(result, planCode) {
      const curPlan = _.get(selectedPlanMap, planCode, {});
      const isOneMonth = _.get(curPlan, 'isOneMonth', false);
      const billingCycle = _.get(curPlan, 'billing_cycle');

      // one month or one time plan, don't have discount in next invoice date
      if (isOneMonth || BILLING_CYCLE.ONE_TIME === billingCycle) return result;

      const couponExpireDate = _.get(planWithDiscount, [planCode, 'expireDate']);
      // set PDT offset -7
      const { nextAnnual, nextMonthly, nextQuarterly, nextTwoYears, nextThreeYears } = getNextPstCycleDate({
        offset: -7,
      });
      const curDiscount = _.get(planWithDiscount, [planCode, 'discountAmount'], 0);

      if (billingCycle === BILLING_CYCLE.MONTHLY && compareDate(couponExpireDate, nextMonthly)) {
        result.monthlyDiscount = result.monthlyDiscount + curDiscount;
      } else if (billingCycle === BILLING_CYCLE.ANNUAL && compareDate(couponExpireDate, nextAnnual)) {
        result.annualDiscount = result.annualDiscount + curDiscount;
      } else if (billingCycle === BILLING_CYCLE.QUARTERLY && compareDate(couponExpireDate, nextQuarterly)) {
        result.quarterlyDiscount += curDiscount;
      } else if (billingCycle === BILLING_CYCLE.TWO_YEARS && compareDate(couponExpireDate, nextTwoYears)) {
        result.twoYearsDiscount += curDiscount;
      } else if (billingCycle === BILLING_CYCLE.THREE_YEARS && compareDate(couponExpireDate, nextThreeYears)) {
        result.threeYearsDiscount += curDiscount;
      }

      return result;
    }, defaultConfig);

    const monthlyCharge = monthlyAmount - discountMap.monthlyDiscount;
    const annualCharge = annualAmount - discountMap.annualDiscount;
    const quarterlyCharge = quarterlyAmount - discountMap.quarterlyAmount;
    const twoYearsCharge = twoYearsAmount - discountMap.twoYearsAmount;
    const threeYearsCharge = threeYearsAmount - discountMap.threeYearsAmount;

    return {
      ...discountMap,
      monthlyCharge,
      annualCharge,
      quarterlyCharge,
      twoYearsCharge,
      threeYearsCharge,
    };
  }

  return {
    ...defaultConfig,
    monthlyCharge: monthlyAmount,
    annualCharge: annualAmount,
    quarterlyCharge: quarterlyAmount,
    twoYearsCharge: twoYearsAmount,
    threeYearsCharge: threeYearsAmount,
  };
}
